import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

export type UserRole = 'admin' | 'premium' | 'free' | 'clinician';

export const useUserRole = (user: User | null) => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasClinicianConnection, setHasClinicianConnection] = useState(false);

  useEffect(() => {
    const fetchRoles = async () => {
      if (!user) {
        setRoles([]);
        setHasClinicianConnection(false);
        setIsLoading(false);
        return;
      }

      try {
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id);

        if (roleError) {
          throw roleError;
        }

        setRoles(roleData?.map(r => r.role as UserRole) || []);

        const { data: clientData } = await supabase
          .from('clinician_clients')
          .select('id')
          .eq('client_id', user.id)
          .eq('status', 'active')
          .limit(1);

        setHasClinicianConnection((clientData?.length || 0) > 0);

      } catch {
        setRoles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoles();
  }, [user]);

  const isAdmin = roles.includes('admin');
  const isPremium = roles.includes('premium') || isAdmin;
  const isFree = roles.includes('free');
  const isClinician = roles.includes('clinician' as UserRole);

  return {
    roles,
    isAdmin,
    isPremium,
    isFree,
    isClinician,
    hasClinicianConnection,
    isLoading
  };
};
